import { type ReactNode } from 'react';

import { isLowLuminance } from '@/helper/colorHelper';
import { cn } from '@/lib/utils';

import Badge, { type BadgeProps } from './Badge';

type ColorBadgeProps = Omit<BadgeProps, 'color'> & {
  color: string;
  textColor?: 'light' | 'dark' | 'auto';
  children: ReactNode;
};

const getTextColorClass = (color: string, textColor: 'light' | 'dark' | 'auto') => {
  if (textColor === 'light') return 'text-white';
  if (textColor === 'dark') return 'text-gray-900';
  const hexForLuminance = color.length === 9 ? color.substring(0, 7) : color;
  return isLowLuminance(hexForLuminance) ? 'text-white' : 'text-gray-900';
};

const needsLightText = (color: string, textColor: 'light' | 'dark' | 'auto') => {
  if (textColor === 'light') return true;
  if (textColor === 'dark') return false;
  const hexForLuminance = color.length === 9 ? color.substring(0, 7) : color;
  return isLowLuminance(hexForLuminance);
};

const ColorBadge = ({
  color,
  textColor = 'auto',
  variant = 'solid',
  disabled = false,
  className,
  style,
  ...props
}: ColorBadgeProps) => {
  const textClass = disabled ? undefined : getTextColorClass(color, textColor);
  const borderColor = variant === 'outlined' ? color : 'transparent';
  const closeButtonInverse = !disabled && needsLightText(color, textColor);

  return (
    <Badge
      {...props}
      variant={variant}
      color="default"
      disabled={disabled}
      closeButtonInverse={closeButtonInverse}
      className={cn(textClass, className)}
      style={
        disabled
          ? style
          : {
              backgroundColor: color,
              borderColor,
              ...style,
            }
      }
    />
  );
};

export default ColorBadge;
